import { useState, useRef, useCallback } from 'react';
import { Upload, X, Video } from 'lucide-react';
import { uploadMedia } from '../adminApi';
import { toast } from 'sonner';

// ── Helpers ───────────────────────────────────────────────────────────────────

function detectPreviewType(url) {
  if (!url) return null;
  if (/\.(jpg|jpeg|png|webp)(\?.*)?$/i.test(url)) return 'image';
  if (/\.mp4(\?.*)?$/i.test(url)) return 'video';
  return null;
}

const ACCEPT_ATTRS = {
  image: '.jpg,.jpeg,.png,.webp',
  video: '.mp4',
};

const ACCEPT_LABELS = {
  image: 'JPG · PNG · WEBP',
  video: 'MP4',
};

const MAX_MB = { image: 20, video: 100 };

function validateFile(file, accept) {
  const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const videoTypes = ['video/mp4'];
  const valid = accept === 'video' ? videoTypes.includes(file.type) : imageTypes.includes(file.type);
  if (!valid) {
    throw new Error(
      accept === 'video'
        ? 'Sadece MP4 dosyası yükleyebilirsiniz.'
        : 'Sadece JPG, PNG veya WEBP dosyası yükleyebilirsiniz.'
    );
  }
  if (file.size > MAX_MB[accept] * 1024 * 1024) {
    throw new Error(`Dosya çok büyük. Maksimum ${MAX_MB[accept]} MB.`);
  }
}

// ── Full drop-zone upload field ───────────────────────────────────────────────
// Used for cover_image and cover_video fields.
// Shows a drag-and-drop zone + thumbnail preview + manual URL input.

export function UploadField({ value, onChange, onBlur, accept = 'image', folder = 'projects', placeholder }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const previewType = detectPreviewType(value);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    try { validateFile(file, accept); }
    catch (e) { toast.error(e.message); return; }

    setUploading(true);
    try {
      const res = await uploadMedia(file, folder);
      onChange(res.url);
      toast.success('Yüklendi.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  }, [accept, folder, onChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  }, [handleFile]);

  return (
    <div className="space-y-2">
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`relative rounded-lg border border-dashed cursor-pointer transition-all overflow-hidden ${
          dragOver
            ? 'border-[#c5a059]/60 bg-[#c5a059]/5'
            : 'border-white/[0.12] hover:border-white/25 bg-white/[0.02] hover:bg-white/[0.04]'
        } ${uploading ? 'pointer-events-none' : ''}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTRS[accept]}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />

        {/* Image preview */}
        {previewType === 'image' && !uploading && (
          <div className="p-2">
            <img
              src={value}
              alt=""
              onError={(e) => { e.target.style.display = 'none'; }}
              className="max-h-44 max-w-full rounded object-cover mx-auto block"
            />
            <p className="text-[10px] text-white/20 text-center mt-1.5">
              Değiştirmek için tıkla veya sürükle
            </p>
          </div>
        )}

        {/* Video preview */}
        {previewType === 'video' && !uploading && (
          <div className="flex flex-col items-center gap-1.5 py-4">
            <Video size={22} className="text-white/30" />
            <p className="text-xs text-white/45 max-w-xs truncate px-4">
              {value.split('/').pop() || 'video.mp4'}
            </p>
            <p className="text-[10px] text-white/20">Değiştirmek için tıkla</p>
          </div>
        )}

        {/* Upload prompt — shown when no value or uploading */}
        {(!value || (!previewType && !uploading)) && (
          <div className="flex flex-col items-center gap-1.5 py-5">
            {uploading
              ? <div className="w-5 h-5 border-2 border-[#c5a059]/30 border-t-[#c5a059] rounded-full animate-spin" />
              : <Upload size={18} className="text-white/25" />
            }
            <p className="text-xs text-white/30">
              {uploading ? 'Yükleniyor…' : 'Sürükle veya tıkla'}
            </p>
            {!uploading && (
              <p className="text-[10px] text-white/15">
                {ACCEPT_LABELS[accept]} · maks {MAX_MB[accept]} MB
              </p>
            )}
          </div>
        )}

        {/* Overlay spinner when uploading over existing preview */}
        {uploading && previewType && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="w-5 h-5 border-2 border-[#c5a059]/30 border-t-[#c5a059] rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Manual URL input — always visible so existing /images/… paths still work */}
      <div className="flex gap-1.5">
        <input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder || 'URL girin ya da yukarıdan dosya yükleyin…'}
          className="flex-1 min-w-0 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-1.5 text-xs text-white/75 placeholder-white/15 focus:outline-none focus:border-[#c5a059]/50 transition-all"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="p-1.5 text-white/25 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
          >
            <X size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Compact upload button ─────────────────────────────────────────────────────
// Used inline in gallery item rows — just an icon button that triggers a file
// picker and writes the resulting Supabase URL into the adjacent URL input.

export function UploadButton({ onChange, accept = 'image', folder = 'gallery' }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    try { validateFile(file, accept); }
    catch (e) { toast.error(e.message); return; }

    setUploading(true);
    try {
      const res = await uploadMedia(file, folder);
      onChange(res.url);
      toast.success('Yüklendi.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  }, [accept, folder, onChange]);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTRS[accept]}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <button
        type="button"
        onClick={() => !uploading && inputRef.current?.click()}
        disabled={uploading}
        title={`Dosya yükle (${ACCEPT_LABELS[accept]})`}
        className="p-1.5 text-white/30 hover:text-[#c5a059] hover:bg-[#c5a059]/10 rounded-lg transition-colors flex-shrink-0 disabled:opacity-40"
      >
        {uploading
          ? <div className="w-3.5 h-3.5 border border-[#c5a059]/30 border-t-[#c5a059] rounded-full animate-spin" />
          : <Upload size={14} />
        }
      </button>
    </>
  );
}
